import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FieldShell, NativeSelect, ReadOnlyBox } from "./crm-ui";
import { optionLabel, type CrmEntity, type FieldDef } from "@/lib/crm/fields";
import type { CrmMember } from "@/lib/crm/api";
import { useCrmText } from "@/lib/crm/i18n";
import { maskPhone } from "@/lib/masks";
import { maskPostal, type Draft } from "@/lib/crm/form-values";
import { formatCpf } from "@/lib/cpf";
import { maskCnpj } from "@/lib/cnpj";
import type { CrmRecord } from "@/lib/crm/api";
import { ClientLookupField } from "./crm-client-field";
import { ExternalLink } from "lucide-react";

interface Props {
  entity: CrmEntity;
  def: FieldDef;
  value: any;
  onChange: (value: any) => void;
  onBlur?: () => void;
  error?: string;
  required?: boolean;
  readOnly?: boolean;
  members: CrmMember[];
  /** Display name for a user field when the user is no longer a member. */
  userName?: string | null;
  label?: string;
  hint?: React.ReactNode;
  labelAddon?: React.ReactNode;
  /** Lookup (the client of a lead): the picked record, to show its data right away. */
  onPickRecord?: (record: CrmRecord) => void;
  /** Lookup: data used to fill the "new client" form. */
  lookupPrefill?: Draft;
}

export function CrmFieldInput({ entity, def, value, onChange, onBlur, error, required, readOnly, members, userName, label, hint, labelAddon, onPickRecord, lookupPrefill }: Props) {
  const { lang, tx } = useCrmText();
  const id = `crm-${entity}-${def.key}`;
  const text = value === null || value === undefined ? "" : String(value);
  const labelText = label || (lang === "pt" ? def.pt : def.en);
  const testId = `crm-field-${def.key}`;
  const invalid = !!error;

  if (def.type === "lookup") {
    return (
      <ClientLookupField
        value={text || null}
        label={labelText}
        required={required}
        error={error}
        readOnly={readOnly}
        prefill={lookupPrefill}
        testId={testId}
        onPick={(r) => { onChange(r.id); onPickRecord?.(r); }}
      />
    );
  }

  const readOnlyValue = () => {
    if (!text) return "";
    switch (def.type) {
      case "option": return optionLabel(def.key, text, lang);
      case "user": return members.find((m) => m.userId === text)?.name || userName || text;
      case "email": return <a className="underline underline-offset-2 break-all" href={`mailto:${text}`}>{text}</a>;
      case "url": {
        const href = text.includes("://") ? text : `https://${text}`;
        return (
          <a className="inline-flex items-center gap-1 underline underline-offset-2 break-all" href={href} target="_blank" rel="noreferrer">
            {text} <ExternalLink className="h-3 w-3 shrink-0" />
          </a>
        );
      }
      default: return <span className="whitespace-pre-wrap">{text}</span>;
    }
  };

  let control: React.ReactNode;
  if (readOnly) {
    control = <ReadOnlyBox value={readOnlyValue()} testId={testId} />;
  } else {
    switch (def.type) {
      case "option":
        control = (
          <NativeSelect id={id} value={text} onChange={(e) => onChange(e.target.value)} onBlur={onBlur} aria-invalid={invalid} data-testid={testId}>
            <option value="">{tx("select")}</option>
            {(def.options || []).map((o) => <option key={o} value={o}>{optionLabel(def.key, o, lang)}</option>)}
          </NativeSelect>
        );
        break;
      case "user": {
        const known = members.some((m) => m.userId === text);
        control = (
          <NativeSelect id={id} value={text} onChange={(e) => onChange(e.target.value)} onBlur={onBlur} aria-invalid={invalid} data-testid={testId}>
            <option value="">{tx("select")}</option>
            {!known && text && <option value={text}>{userName || text}</option>}
            {members.map((m) => <option key={m.userId} value={m.userId}>{m.name}</option>)}
          </NativeSelect>
        );
        break;
      }
      case "textarea":
        control = (
          <Textarea id={id} rows={4} value={text} maxLength={def.maxLength} onChange={(e) => onChange(e.target.value)} onBlur={onBlur} aria-invalid={invalid} data-testid={testId} />
        );
        break;
      case "phone":
        control = (
          <Input id={id} type="tel" inputMode="tel" placeholder="(31) 99999-9999" value={text}
            onChange={(e) => onChange(maskPhone(e.target.value))} onBlur={onBlur} aria-invalid={invalid} data-testid={testId} />
        );
        break;
      case "email":
        control = (
          <Input id={id} type="email" inputMode="email" placeholder="nome@empresa.com.br" value={text} maxLength={def.maxLength}
            onChange={(e) => onChange(e.target.value)} onBlur={onBlur} aria-invalid={invalid} data-testid={testId} />
        );
        break;
      case "url":
        control = (
          <Input id={id} type="url" inputMode="url" placeholder="https://" value={text} maxLength={def.maxLength}
            onChange={(e) => onChange(e.target.value)} onBlur={onBlur} aria-invalid={invalid} data-testid={testId} />
        );
        break;
      case "number":
        control = (
          <Input id={id} inputMode="numeric" value={text} onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 9))}
            onBlur={onBlur} aria-invalid={invalid} data-testid={testId} />
        );
        break;
      case "cpf":
        control = (
          <Input id={id} inputMode="numeric" placeholder="000.000.000-00" value={text} maxLength={14}
            onChange={(e) => onChange(formatCpf(e.target.value))} onBlur={onBlur} aria-invalid={invalid} data-testid={testId} />
        );
        break;
      case "cnpj":
        control = (
          <Input id={id} placeholder="00.000.000/0000-00" value={text} maxLength={18} autoCapitalize="characters"
            onChange={(e) => onChange(maskCnpj(e.target.value))} onBlur={onBlur} aria-invalid={invalid} data-testid={testId} />
        );
        break;
      case "postalCode":
        control = (
          <Input id={id} inputMode="numeric" placeholder="00000-000" value={text} maxLength={def.maxLength}
            onChange={(e) => onChange(maskPostal(e.target.value))} onBlur={onBlur} aria-invalid={invalid} data-testid={testId} />
        );
        break;
      default:
        control = (
          <Input id={id} value={text} maxLength={def.maxLength} onChange={(e) => onChange(e.target.value)} onBlur={onBlur}
            aria-invalid={invalid} data-testid={testId} />
        );
    }
  }

  return (
    <FieldShell label={labelText} required={required} error={error} htmlFor={readOnly ? undefined : id} hint={hint} labelAddon={labelAddon}>
      {control}
    </FieldShell>
  );
}
